/**
 * Zoom-style emoji reaction strip for live sessions.
 */

import React, { useEffect, useRef } from "react";
import { LIVE_REACTION_EMOJIS } from "./liveReactionTypes";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string) => void;
};

export function ReactionPicker({ open, onClose, onSelect }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Send a reaction"
      style={{
        position: "absolute",
        bottom: "calc(100% + 12px)",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 999,
        background: "#2a2d36",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "4px 6px",
          borderRadius: 999,
          background: "#3a3d48",
        }}
      >
        {LIVE_REACTION_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            title={emoji}
            onClick={() => {
              onSelect(emoji);
              onClose();
            }}
            style={{
              border: "none",
              borderRadius: 999,
              background: "transparent",
              fontSize: 22,
              lineHeight: 1,
              padding: "6px 7px",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
              e.currentTarget.style.transform = "scale(1.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}
